import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'

interface ProductData {
  name: string
  description: string
  price: number
  image: string
  sku: string
  url?: string
}

interface SEOSchemaMarkupProps {
  type?:
    | 'organization'
    | 'website'
    | 'faq'
    | 'breadcrumb'
    | 'product'
    | 'productList'
  faqData?: Array<{ question: string; answer: string }>
  productData?: ProductData
  productsData?: ProductData[]
}

const SEOSchemaMarkup = ({
  type = 'organization',
  faqData,
  productData,
  productsData,
}: SEOSchemaMarkupProps) => {
  const location = useLocation()
  const baseUrl =
    typeof window !== 'undefined'
      ? window.location.origin
      : 'https://fotolivro.store'

  useEffect(() => {
    const schemas: object[] = []

    // Organization Schema - always include
    const organizationSchema = {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      '@id': `${baseUrl}/#organization`,
      name: 'FotoLivro Store',
      alternateName: 'FotoLivro',
      url: baseUrl,
      logo: {
        '@type': 'ImageObject',
        url: `${baseUrl}/logo.png`,
        width: 200,
        height: 60,
      },
      description:
        'Fotolivros personalizados de luxo com design exclusivo, edição profissional e impressão premium. Frete grátis para todo o Brasil.',
      address: {
        '@type': 'PostalAddress',
        streetAddress: 'Rua Imaculada Conceição, 1430',
        addressLocality: 'Curitiba',
        addressRegion: 'PR',
        postalCode: '80215-901',
        addressCountry: 'BR',
      },
      contactPoint: {
        '@type': 'ContactPoint',
        contactType: 'customer service',
        email: 'atendimento@fotolivro.store',
        telephone: '+55-41-99198-5765',
        availableLanguage: ['Portuguese'],
      },
      sameAs: [
        'https://instagram.com/fotolivrostore',
        'https://youtube.com/@fotolivrostore',
        'https://www.linkedin.com/company/fotolivrostore/',
      ],
      foundingDate: '2010',
      numberOfEmployees: {
        '@type': 'QuantitativeValue',
        minValue: 5,
        maxValue: 20,
      },
    }

    // WebSite Schema
    const websiteSchema = {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      '@id': `${baseUrl}/#website`,
      url: baseUrl,
      name: 'FotoLivro Store',
      description:
        'Fotolivros personalizados de luxo com design exclusivo e impressão premium',
      publisher: {
        '@id': `${baseUrl}/#organization`,
      },
      inLanguage: 'pt-BR',
      potentialAction: {
        '@type': 'SearchAction',
        target: {
          '@type': 'EntryPoint',
          urlTemplate: `${baseUrl}/blog?search={search_term_string}`,
        },
        'query-input': 'required name=search_term_string',
      },
    }

    // LocalBusiness Schema
    const localBusinessSchema = {
      '@context': 'https://schema.org',
      '@type': 'LocalBusiness',
      '@id': `${baseUrl}/#localbusiness`,
      name: 'FotoLivro Store',
      image: `${baseUrl}/logo.png`,
      url: baseUrl,
      telephone: '+55-41-99198-5765',
      email: 'atendimento@fotolivro.store',
      address: {
        '@type': 'PostalAddress',
        streetAddress: 'Rua Imaculada Conceição, 1430, Prado Velho',
        addressLocality: 'Curitiba',
        addressRegion: 'PR',
        postalCode: '80215-901',
        addressCountry: 'BR',
      },
      geo: {
        '@type': 'GeoCoordinates',
        latitude: -25.448,
        longitude: -49.2357,
      },
      priceRange: '$$',
      openingHoursSpecification: {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
        opens: '09:00',
        closes: '18:00',
      },
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: '4.9',
        reviewCount: '127',
        bestRating: '5',
        worstRating: '1',
      },
    }

    schemas.push(organizationSchema, websiteSchema, localBusinessSchema)

    // FAQPage Schema
    if (type === 'faq' && faqData && faqData.length > 0) {
      const faqSchema = {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: faqData.map(item => ({
          '@type': 'Question',
          name: item.question,
          acceptedAnswer: {
            '@type': 'Answer',
            text: item.answer,
          },
        })),
      }
      schemas.push(faqSchema)
    }

    // Product Schema (single product)
    if (type === 'product' && productData) {
      const productSchema = {
        '@context': 'https://schema.org',
        '@type': 'Product',
        name: productData.name,
        description: productData.description,
        image: productData.image,
        sku: productData.sku,
        brand: {
          '@type': 'Brand',
          name: 'FotoLivro Store',
        },
        offers: {
          '@type': 'Offer',
          url: productData.url || `${baseUrl}/#produtos`,
          priceCurrency: 'BRL',
          price: productData.price,
          availability: 'https://schema.org/InStock',
          seller: {
            '@id': `${baseUrl}/#organization`,
          },
        },
        aggregateRating: {
          '@type': 'AggregateRating',
          ratingValue: '4.9',
          reviewCount: '127',
          bestRating: '5',
          worstRating: '1',
        },
      }
      schemas.push(productSchema)
    }

    // Product List Schema (multiple products for home page)
    if (type === 'productList' && productsData && productsData.length > 0) {
      const productListSchema = {
        '@context': 'https://schema.org',
        '@type': 'ItemList',
        name: 'Fotolivros FotoLivro Store',
        description: 'Coleção de fotolivros personalizados premium',
        numberOfItems: productsData.length,
        itemListElement: productsData.map((product, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          item: {
            '@type': 'Product',
            name: product.name,
            description: product.description,
            image: product.image,
            sku: product.sku,
            brand: {
              '@type': 'Brand',
              name: 'FotoLivro Store',
            },
            offers: {
              '@type': 'Offer',
              url: product.url || `${baseUrl}/#produtos`,
              priceCurrency: 'BRL',
              price: product.price,
              availability: 'https://schema.org/InStock',
              priceValidUntil: new Date(
                new Date().setFullYear(new Date().getFullYear() + 1),
              )
                .toISOString()
                .split('T')[0],
            },
            aggregateRating: {
              '@type': 'AggregateRating',
              ratingValue: '4.9',
              reviewCount: '127',
              bestRating: '5',
              worstRating: '1',
            },
          },
        })),
      }
      schemas.push(productListSchema)
    }

    // Remove existing schema scripts
    const existingScripts = document.querySelectorAll(
      'script[data-schema-markup]',
    )
    existingScripts.forEach(script => script.remove())

    // Add new schema scripts
    schemas.forEach((schema, index) => {
      const script = document.createElement('script')
      script.type = 'application/ld+json'
      script.setAttribute('data-schema-markup', `schema-${index}`)
      script.text = JSON.stringify(schema)
      document.head.appendChild(script)
    })

    return () => {
      const scriptsToRemove = document.querySelectorAll(
        'script[data-schema-markup]',
      )
      scriptsToRemove.forEach(script => script.remove())
    }
  }, [type, faqData, productData, productsData, baseUrl, location.pathname])

  return null
}

export default SEOSchemaMarkup
